<script lang="ts">
  import { Link, navigate } from 'svelte-routing';
  import { api } from '../lib/api/client';
  import type { PersonView } from '../lib/api/types';
  import { groups } from '../lib/stores/groups';
  import { isAuthenticated } from '../lib/stores/auth';
  import { when } from '../lib/utils/time';
  import PageHeader from '../lib/components/PageHeader.svelte';
  import { t } from '../lib/i18n/index.ts';

  // Person is what one person gets of the member across every group both
  // belong to (spec 001 FR-076, 001 FR-077, 001 FR-078). It only shows; each group it
  // names opens that group's sharing to change it (001 FR-081).
  let { userId }: { userId: string } = $props();

  let view = $state<PersonView | null>(null);
  let error = $state('');

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  $effect(() => {
    const id = userId;
    api.get<PersonView>(`/me/people/${id}`).then((v) => (view = v)).catch((e) => (error = e.message));
  });

  const levels: Record<string, string> = {
    detailed: t('personLevelDetailed'),
    standard: t('personLevelStandard'),
    choose: t('personLevelChoose'),
    off: t('personNone'),
    paused: t('personLevelPaused'),
  };

  const items: { key: keyof PersonView['items']; name: string }[] = [
    { key: 'activities', name: t('personItemActivities') },
    { key: 'heart_rate', name: t('personItemHeartRate') },
    { key: 'health', name: t('personItemHealth') },
    { key: 'rest_sleep', name: t('personItemRestSleep') },
    { key: 'driving', name: t('personItemDriving') },
    { key: 'phone', name: t('personItemPhone') },
    { key: 'email', name: t('personItemEmail') },
  ];

  // fromParts is personFrom split around the list of groups, which are links.
  const fromParts = t('personFrom', { groups: '\u0000' }).split('\u0000');

  function reach(days: number): string {
    if (days < 0) return t('personNone');
    if (days === 0) return t('personHistoryAll');
    return t('personHistoryDays', { days });
  }

  function locationText(v: PersonView): string {
    const level = levels[v.location.level];
    return v.location.detailed_until ? t('personDetailedUntil', { level, when: when(v.location.detailed_until) }) : level;
  }

  function nameOf(id: string): string {
    return view?.groups.find((g) => g.group_id === id)?.name ?? '';
  }

  // open shows a group's sharing, where the item is changed.
  function open(id: string) {
    groups.selected.set(id);
    navigate('/groups');
  }
</script>

<div class="p-6 max-w-2xl w-full mx-auto">
  <Link to="/groups" class="text-sm hover:opacity-80"><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{t('personBack')}</Link>
  {#if error}
    <p class="mt-4 text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if view}
    <PageHeader title={t('personTitle', { member: view.display_name })} />
    <p class="text-sm text-text-muted mb-4">
      {t('personIntro', { member: view.display_name })}
    </p>

    {#snippet from(ids: string[])}
      {#if ids.length === 0}
        <span class="text-text-muted">{t('personNoGroup')}</span>
      {:else}
        {#each ids as id, i (id)}{#if i > 0}{t('personGroupSeparator')}{/if}<button class="underline hover:opacity-80" onclick={() => open(id)}>{nameOf(id)}</button>{/each}
      {/if}
    {/snippet}

    <div class="card p-5 mb-4">
      <dl class="divide-y" style="border-color: var(--theme-surface1);">
        <div class="py-3">
          <dt class="font-medium">{t('personLocation')}</dt>
          <dd class="text-sm">{locationText(view)}</dd>
          {#if view.location.groups.length}<dd class="text-xs text-text-muted">{fromParts[0]}{@render from(view.location.groups)}{fromParts[1]}</dd>{/if}
        </div>
        <div class="py-3">
          <dt class="font-medium">{t('personHistory')}</dt>
          <dd class="text-sm">{reach(view.lookback.days)}</dd>
          {#if view.lookback.groups.length}<dd class="text-xs text-text-muted">{fromParts[0]}{@render from(view.lookback.groups)}{fromParts[1]}</dd>{/if}
        </div>
        {#each items as it (it.key)}
          <div class="py-3 flex items-baseline gap-3">
            <dt class="font-medium flex-1">{it.name}</dt>
            <dd class="text-sm">{#if view.items[it.key].length}{@render from(view.items[it.key])}{:else}<span class="text-text-muted">{t('personNotShared')}</span>{/if}</dd>
          </div>
        {/each}
      </dl>
    </div>

    <div class="card p-5">
      <PageHeader title={t('personGroups')} level="section" />
      <ul class="text-sm">
        {#each view.groups as g (g.group_id)}
          <li class="py-2 flex items-center gap-3">
            <button class="flex-1 text-left underline hover:opacity-80" onclick={() => open(g.group_id)}>{g.name}</button>
            {#if g.paused}
              <span class="text-xs" style="color: var(--theme-warning);">{g.paused_until ? t('personPausedUntil', { when: when(g.paused_until) }) : t('personPausedUntilResume')}</span>
            {/if}
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</div>
